import type {
  MiAttackCluster,
  MiCommentary,
  MiFocusCluster,
  MiHeaderStat,
  MiKwCluster,
  MiLandingBlueprintItem,
  MiMarketData,
  MiOfferUsage,
  MiOverviewMetric,
  MiSegmentStat,
  MiSeoKeyword,
  MiSeoMetric,
  MiSeoStrategy,
  MiSignalGroup,
  MiSwotItem,
  MiToneKey,
} from '@elva/ui';
import { growthGet } from '@/data/http';
import { num, relativeTimeFromIso, str } from '@/data/adapt';

interface BeSignalItem {
  pct?: number;
  behavior?: string;
  feature?: string;
  type?: string;
}

interface BeMarketSignals {
  review_patterns?: BeSignalItem[];
  booking_maturity?: BeSignalItem[];
  digital_presence?: BeSignalItem[];
  offer_prevalence?: BeSignalItem[];
  insurance_posture?: BeSignalItem[];
}

interface BeMarketSummaryDetail {
  total_competitors?: number;
  total_active_competitors?: number;
  radius_km?: number;
  ads_stats?: {
    keywords_with_no_ads?: string[];
    competitors_running_ads?: number;
    most_advertised_keywords?: string[];
    most_advertised_categories?: string[];
    avg_active_ads_per_competitor?: number;
  };
  review_stats?: {
    market_avg_rating?: number;
    market_avg_reviews?: number;
    market_max_reviews?: number;
    our_rank_by_rating?: number;
    our_rank_by_reviews?: number;
    market_max_reviews_clinic?: string;
    market_avg_velocity_30d?: number | null;
    our_velocity_30d?: number | null;
  };
  content_stats?: {
    market_avg_pages?: number;
    market_avg_blog_posts?: number;
    market_avg_content_depth_score?: number;
    competitors_with_faq?: number;
    competitors_with_schema?: number;
    competitors_with_active_blog?: number;
  };
  service_coverage?: {
    pct_offering_emergency?: number;
    pct_offering_pediatric?: number;
    pct_offering_implants?: number;
    pct_offering_invisalign?: number;
    pct_offering_sedation?: number;
  };
  operations_stats?: Record<string, unknown>;
  market_signals?: BeMarketSignals;
}

interface BeSwotLists {
  strengths?: string[];
  weaknesses?: string[];
  opportunities?: string[];
  threats?: string[];
}

interface BeSwotInner {
  swot?: BeSwotLists;
  ai_commentary?: {
    whats_happening?: string;
    how_autopilot_responds?: string;
    human_decisions_needed?: string;
  };
  action_brief?: {
    bottom_line?: string;
    priorities?: Array<{
      title?: string;
      description?: string;
      urgency?: string;
      expected_impact?: string;
    }>;
  };
  ads_market_strategy?: {
    offer_usage_in_market?: Array<{ offer?: string; count?: number; label?: string }>;
    recommended_focus_clusters?: Array<{ label?: string; reason?: string; cluster?: string }>;
    budget_split_recommendation?: {
      narrative?: string;
      splits?: Array<{ label?: string; pct?: number; share?: number }>;
    };
    keyword_cluster_competition?: Array<{ name?: string; keyword?: string; value?: number; score?: number }>;
  };
  seo_market_strategy?: {
    pages_to_build?: string[];
    pages_to_watch?: string[];
    content_to_build?: string[];
    common_local_gaps?: string[];
    keyword_opportunities?: Array<{
      keyword?: string;
      intent?: string;
      difficulty?: string | number;
      page?: string;
      note?: string;
    }>;
  };
  market_signals?: BeMarketSignals;
}

interface BeMarketIntelligence {
  clinic_id?: number;
  external_id?: string;
  market_summary?: {
    included_competitors?: number;
    total_active_competitors?: number;
    last_synced_at?: string;
    radius_km?: number;
    summary?: BeMarketSummaryDetail;
  };
  market_swot?: {
    last_synced_at?: string;
    competitors_included?: number;
    swot?: BeSwotInner;
  };
  market_ads?: {
    summary?: {
      total_ads?: number;
      by_competitor?: Array<{ business_name?: string; ad_count?: number }>;
      by_category?: Array<{ category?: string; count?: number }>;
    };
    results?: Array<{
      business_name?: string;
      primary_offer_category?: string;
      cta_text?: string;
      serp?: { keyword?: string; query_text?: string };
      raw_ad?: { headlines?: string[]; descriptions?: string[] };
    }>;
  };
  top_keywords?: Array<{
    keyword?: string;
    search_volume_monthly?: number;
    intent?: string;
    difficulty?: string | number;
    page?: string;
    note?: string;
  }>;
  data_freshness?: Record<string, string>;
}

const EMPTY_SWOT: MiSwotItem[] = [
  { title: 'Strengths', tone: 'success', items: [] },
  { title: 'Weaknesses', tone: 'terra', items: [] },
  { title: 'Opportunities', tone: 'info', items: [] },
  { title: 'Threats', tone: 'gold', items: [] },
];

const EMPTY_SIGNALS: MiSignalGroup[] = [
  { label: 'Offer prevalence', items: [] },
  { label: 'Booking maturity', items: [] },
  { label: 'Digital presence', items: [] },
  { label: 'Insurance posture', items: [] },
  { label: 'Review patterns', items: [] },
];

function toList(value: string | string[] | null | undefined): string[] {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (value?.trim()) return [value.trim()];
  return [];
}

function signalLabel(item: BeSignalItem): string {
  return str(item.feature ?? item.behavior ?? item.type, '—');
}

function signalPct(item: BeSignalItem): number {
  const pct = num(item.pct);
  return pct <= 1 && pct > 0 ? Math.round(pct * 100) : Math.round(pct);
}

function adaptSignals(raw: BeMarketSignals | undefined): MiSignalGroup[] {
  if (!raw) return EMPTY_SIGNALS;
  return [
    {
      label: 'Offer prevalence',
      items: (raw.offer_prevalence ?? []).map((item) => ({
        name: signalLabel(item),
        value: signalPct(item),
      })),
    },
    {
      label: 'Booking maturity',
      items: (raw.booking_maturity ?? []).map((item) => ({
        name: signalLabel(item),
        value: signalPct(item),
      })),
    },
    {
      label: 'Digital presence',
      items: (raw.digital_presence ?? []).map((item) => ({
        name: signalLabel(item),
        value: signalPct(item),
      })),
    },
    {
      label: 'Insurance posture',
      items: (raw.insurance_posture ?? []).map((item) => ({
        name: signalLabel(item),
        value: signalPct(item),
      })),
    },
    {
      label: 'Review patterns',
      items: (raw.review_patterns ?? []).map((item) => ({
        name: signalLabel(item),
        value: signalPct(item),
      })),
    },
  ];
}

function urgencyTone(urgency: string | undefined): MiToneKey {
  const u = (urgency ?? '').toLowerCase();
  if (u === 'high') return 'terra';
  if (u === 'medium') return 'gold';
  return 'info';
}

function clusterTone(index: number): MiToneKey {
  const tones: MiToneKey[] = ['ai', 'terra', 'gold', 'info', 'success'];
  return tones[index % tones.length] ?? 'ai';
}

function adaptHeaderStats(be: BeMarketIntelligence): MiHeaderStat[] {
  const summary = be.market_summary;
  const detail = summary?.summary;
  const freshness =
    be.data_freshness?.market_summary ??
    summary?.last_synced_at ??
    be.market_swot?.last_synced_at;
  const tracked = num(
    summary?.included_competitors ?? detail?.total_competitors ?? be.market_swot?.competitors_included,
  );
  const sources = num(
    detail?.total_active_competitors ?? summary?.total_active_competitors,
    tracked,
  );

  return [
    { label: 'Data sources analyzed', value: sources > 0 ? String(sources) : '—' },
    { label: 'Competitors tracked', value: tracked > 0 ? String(tracked) : '—' },
    {
      label: 'Last updated',
      value: freshness ? relativeTimeFromIso(freshness) : '—',
      accent: true,
    },
  ];
}

function adaptOverviewMetrics(detail: BeMarketSummaryDetail | undefined): MiOverviewMetric[] {
  const review = detail?.review_stats;
  const ads = detail?.ads_stats;
  const content = detail?.content_stats;
  const total = num(detail?.total_competitors);
  const active = num(detail?.total_active_competitors);

  return [
    {
      label: 'Total competitors',
      value: total > 0 ? String(total) : '—',
      delta: active > 0 ? `${active} active in market` : '—',
      tone: 'neutral',
    },
    {
      label: 'Market avg rating',
      value: review?.market_avg_rating != null ? String(review.market_avg_rating) : '—',
      delta:
        review?.our_rank_by_rating != null ? `Your rank: #${review.our_rank_by_rating}` : '—',
      tone: 'neutral',
    },
    {
      label: 'Competitors running ads',
      value: ads?.competitors_running_ads != null ? String(ads.competitors_running_ads) : '—',
      delta:
        ads?.avg_active_ads_per_competitor != null
          ? `Avg ${ads.avg_active_ads_per_competitor} ads per competitor`
          : '—',
      tone: 'neutral',
    },
    {
      label: 'Content depth score',
      value:
        content?.market_avg_content_depth_score != null
          ? String(content.market_avg_content_depth_score)
          : '—',
      delta:
        review?.market_avg_reviews != null
          ? `Avg reviews: ${review.market_avg_reviews}`
          : '—',
      tone: 'success',
    },
  ];
}

function adaptSwot(inner: BeSwotInner | undefined): MiSwotItem[] {
  const swot = inner?.swot;
  if (!swot) return EMPTY_SWOT;
  return [
    { title: 'Strengths', tone: 'success', items: swot.strengths ?? [] },
    { title: 'Weaknesses', tone: 'terra', items: swot.weaknesses ?? [] },
    { title: 'Opportunities', tone: 'info', items: swot.opportunities ?? [] },
    { title: 'Threats', tone: 'gold', items: swot.threats ?? [] },
  ];
}

function adaptCommentary(inner: BeSwotInner | undefined): MiCommentary {
  const ai = inner?.ai_commentary;
  const priorities =
    inner?.action_brief?.priorities?.map(
      (p) => `${str(p.title)} — ${str(p.description, '')}`.trim(),
    ) ?? [];
  const recommended = [
    ...toList(ai?.human_decisions_needed),
    ...priorities,
  ];
  if (inner?.action_brief?.bottom_line) {
    recommended.unshift(inner.action_brief.bottom_line);
  }

  return {
    happening: toList(ai?.whats_happening),
    responding: toList(ai?.how_autopilot_responds),
    recommended,
  };
}

function adaptKwClusters(detail: BeMarketSummaryDetail | undefined): MiKwCluster[] {
  const categories = detail?.ads_stats?.most_advertised_categories ?? [];
  if (categories.length) {
    return categories.map((keyword, index) => ({
      keyword,
      share: Math.max(10, 100 - index * 15),
      tone: clusterTone(index),
    }));
  }
  const keywords = detail?.ads_stats?.most_advertised_keywords ?? [];
  return keywords.map((keyword, index) => ({
    keyword,
    share: Math.max(10, 100 - index * 12),
    tone: clusterTone(index),
  }));
}

function adaptOffers(
  ads: BeMarketIntelligence['market_ads'],
  inner: BeSwotInner | undefined,
): MiOfferUsage[] {
  const fromCompetitors =
    ads?.summary?.by_competitor?.map((row) => ({
      offer: str(row.business_name),
      count: num(row.ad_count),
    })) ?? [];
  if (fromCompetitors.length) return fromCompetitors;

  const fromCategories =
    ads?.summary?.by_category?.map((row) => ({
      offer: str(row.category),
      count: num(row.count),
    })) ?? [];
  if (fromCategories.length) return fromCategories;

  return (
    inner?.ads_market_strategy?.offer_usage_in_market?.map((row) => ({
      offer: str(row.offer ?? row.label),
      count: num(row.count),
    })) ?? []
  );
}

function adaptFocusClusters(inner: BeSwotInner | undefined): MiFocusCluster[] {
  const fromStrategy =
    inner?.ads_market_strategy?.recommended_focus_clusters?.map((row, index) => ({
      label: str(row.label ?? row.cluster),
      tone: clusterTone(index),
      reason: str(row.reason, '—'),
    })) ?? [];
  if (fromStrategy.length) return fromStrategy;

  return (
    inner?.action_brief?.priorities?.map((row) => ({
      label: str(row.title),
      tone: urgencyTone(row.urgency),
      reason: str(row.expected_impact ?? row.description, '—'),
    })) ?? []
  );
}

function adaptAttackClusters(inner: BeSwotInner | undefined): MiAttackCluster[] {
  return (
    inner?.ads_market_strategy?.keyword_cluster_competition?.map((row) => ({
      name: str(row.name ?? row.keyword),
      value: num(row.value ?? row.score),
    })) ?? []
  );
}

function adaptBudgetSplit(inner: BeSwotInner | undefined) {
  const splits = inner?.ads_market_strategy?.budget_split_recommendation?.splits ?? [];
  if (!splits.length) return [];
  const tones: MiToneKey[] = ['ai', 'gold', 'lav', 'info'];
  return splits.map((split, index) => ({
    label: str(split.label, `${num(split.pct ?? split.share)}%`),
    flex: Math.max(num(split.pct ?? split.share, 1), 1),
    tone: tones[index % tones.length] ?? 'ai',
  }));
}

function adaptAdBlueprint(inner: BeSwotInner | undefined): string[] {
  const narrative = inner?.ads_market_strategy?.budget_split_recommendation?.narrative;
  const lines = narrative ? [narrative] : [];
  return lines;
}

function adaptLandingBlueprint(): MiLandingBlueprintItem[] {
  return [];
}

function adaptSeoMetrics(detail: BeMarketSummaryDetail | undefined): MiSeoMetric[] {
  const content = detail?.content_stats;
  const review = detail?.review_stats;
  const gaps = detail?.ads_stats?.keywords_with_no_ads ?? [];

  const contentTags: string[] = [];
  if (content?.competitors_with_faq) contentTags.push('FAQ content');
  if (content?.competitors_with_schema) contentTags.push('Schema markup');
  if (content?.competitors_with_active_blog) contentTags.push('Active blog');

  const metrics: MiSeoMetric[] = [
    {
      label: 'Market content depth',
      tone: 'ai',
      ...(content?.market_avg_content_depth_score != null
        ? { value: String(content.market_avg_content_depth_score) }
        : {}),
      ...(content?.market_avg_pages != null ? { sub: `Avg ${content.market_avg_pages} pages` } : {}),
      ...(contentTags.length ? { tags: contentTags } : {}),
    },
    {
      label: 'Review position',
      ...(review?.our_rank_by_reviews != null ? { value: `#${review.our_rank_by_reviews}` } : {}),
      ...(review?.market_max_reviews_clinic
        ? { sub: `Leader: ${review.market_max_reviews_clinic}` }
        : {}),
    },
    {
      label: 'Market avg rating',
      ...(review?.market_avg_rating != null ? { value: String(review.market_avg_rating) } : {}),
      ...(review?.market_avg_reviews != null
        ? { sub: `${review.market_avg_reviews} avg reviews` }
        : {}),
    },
    {
      label: 'Keyword gaps',
      tone: 'gold',
      ...(gaps.length ? { tags: gaps.slice(0, 4) } : {}),
    },
  ];

  return metrics;
}

function adaptSeoKeywords(
  topKeywords: BeMarketIntelligence['top_keywords'],
  inner: BeSwotInner | undefined,
): MiSeoKeyword[] {
  if (topKeywords === null) return [];

  const fromTop =
    topKeywords?.map((row) => ({
      keyword: str(row.keyword),
      intent: str(row.intent, '—'),
      difficulty: row.difficulty != null ? String(row.difficulty) : '—',
      page: str(row.page, '—'),
      note:
        row.note ??
        (row.search_volume_monthly != null ? `~${row.search_volume_monthly} searches/mo` : '—'),
    })) ?? [];
  if (fromTop.length) return fromTop;

  return (
    inner?.seo_market_strategy?.keyword_opportunities?.map((row) => ({
      keyword: str(row.keyword),
      intent: str(row.intent, '—'),
      difficulty: row.difficulty != null ? String(row.difficulty) : '—',
      page: str(row.page, '—'),
      note: str(row.note, '—'),
    })) ?? []
  );
}

function adaptSeoStrategy(inner: BeSwotInner | undefined): MiSeoStrategy {
  const seo = inner?.seo_market_strategy;
  return {
    content: seo?.content_to_build ?? [],
    pages: seo?.pages_to_build ?? [],
    watch: [...(seo?.pages_to_watch ?? []), ...(seo?.common_local_gaps ?? [])],
  };
}

function adaptSegments(detail: BeMarketSummaryDetail | undefined): string[] {
  const coverage = detail?.service_coverage;
  if (!coverage) return [];
  const names: string[] = [];
  if (num(coverage.pct_offering_emergency) > 0) names.push('Emergency');
  if (num(coverage.pct_offering_pediatric) > 0) names.push('Pediatric');
  if (num(coverage.pct_offering_implants) > 0) names.push('Implants');
  if (num(coverage.pct_offering_invisalign) > 0) names.push('Cosmetic');
  if (num(coverage.pct_offering_sedation) > 0) names.push('Sedation');
  return names;
}

function adaptSegmentStats(detail: BeMarketSummaryDetail | undefined): MiSegmentStat[] {
  const radius = detail?.radius_km;
  const total = num(detail?.total_competitors);
  const content = detail?.content_stats;
  return [
    {
      label: 'Market radius',
      value: radius != null ? `${radius} km` : '—',
      sub: total > 0 ? `${total} competitors in scope` : '—',
    },
    {
      label: 'Avg content depth',
      value:
        content?.market_avg_content_depth_score != null
          ? String(content.market_avg_content_depth_score)
          : '—',
      sub:
        content?.market_avg_blog_posts != null
          ? `${content.market_avg_blog_posts} avg blog posts`
          : '—',
    },
    {
      label: 'Service coverage',
      value: detail?.service_coverage ? 'Analyzed' : '—',
      sub: detail?.operations_stats ? 'Operations data available' : '—',
    },
  ];
}

export function adaptMarketIntelligence(be: BeMarketIntelligence): MiMarketData {
  const detail = be.market_summary?.summary;
  const inner = be.market_swot?.swot;
  const tracked = num(
    be.market_summary?.included_competitors ?? detail?.total_competitors ?? be.market_swot?.competitors_included,
  );

  return {
    headerStats: adaptHeaderStats(be),
    overviewMetrics: adaptOverviewMetrics(detail),
    swot: adaptSwot(inner),
    swotSubtitle:
      tracked > 0 ? `Aggregated from ${tracked} tracked competitors` : 'Aggregated market intelligence',
    commentary: adaptCommentary(inner),
    signals: adaptSignals(detail?.market_signals ?? inner?.market_signals),
    kwClusters: adaptKwClusters(detail),
    cpcDays: [],
    offers: adaptOffers(be.market_ads, inner),
    offerCompetitorTotal: Math.max(tracked, 1),
    focusClusters: adaptFocusClusters(inner),
    attackClusters: adaptAttackClusters(inner),
    budgetSplit: adaptBudgetSplit(inner),
    adBlueprint: adaptAdBlueprint(inner),
    landingBlueprint: adaptLandingBlueprint(),
    seoMetrics: adaptSeoMetrics(detail),
    seoKeywords: adaptSeoKeywords(be.top_keywords, inner),
    seoStrategy: adaptSeoStrategy(inner),
    segments: adaptSegments(detail),
    segmentStats: adaptSegmentStats(detail),
  };
}

export async function fetchMarketIntelligence(): Promise<MiMarketData> {
  const be = await growthGet<BeMarketIntelligence>('/market-intelligence/');
  return adaptMarketIntelligence(be);
}
